import { createContext } from 'react';

import type { SelectOption, WorkflowField } from '@/api/workflows';

/**
 * The CLIENT's language — FormShell's `pdfLanguage`, chosen on the Welcome step — for the field
 * renderers below the shell. Distinct from the `language` prop every renderer already takes,
 * which is the ADVISOR's UI language and drives labels.
 *
 * Option language scopes (`SelectOption.languages`, QA D-30) key off THIS one: the use case is a
 * paper form with no box for an option (the French title list has no "Ms."), so what matters is
 * which PDF the client gets, not which language the advisor reads the form in.
 *
 * A context rather than a prop for the same reason as ShowErrorsContext: borrowed reveal fields
 * and repeatable instances hear it without every renderer in between threading it through.
 * `null` (no provider — the builder canvas and other stand-alone renderers) offers every option.
 */
export const ClientLanguageContext = createContext<OfferLanguages>(null);

/**
 * The language(s) an option must be offered in to be shown (every one listed). FormShell passes
 * one: French when either the client's PDF or the advisor's screen is French — see FormShell.
 */
export type OfferLanguages = 'en' | 'fr' | readonly ('en' | 'fr')[] | null;

/** Whether an option is offered in every one of `languages`. Null/empty scope = both languages. */
export function isOptionOffered(option: SelectOption, languages: OfferLanguages): boolean {
  if (!languages) return true;
  const scope = option.languages;
  if (!scope || scope.length === 0) return true;
  const needed = typeof languages === 'string' ? [languages] : languages;
  return needed.every((l) => scope.includes(l));
}

/**
 * The field as the client should see it: its options narrowed to those offered in `language`.
 * Returns the SAME object when nothing is hidden, so a render with no scoped options costs one
 * scan and no allocation.
 */
export function withOfferedOptions(field: WorkflowField, language: OfferLanguages): WorkflowField {
  if (!language || !field.options?.some((o) => !isOptionOffered(o, language))) return field;
  return { ...field, options: field.options.filter((o) => isOptionOffered(o, language)) };
}
